import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { fmtMoney } from '../../../core/utils/format';
import { useEventDashboard, useEventSocket } from '../contexts/event';
import { eventKeys } from '../queries/keys';
import { aggregateBuyers, normalizeCountdown, pickFocal, readCountdown, readEventState } from '../utils/state';

import '../styles/screen.css';

// TSS vite/03 — PANTALLA DE PROYECCIÓN (1920×1080, oscura) del evento de market.
// Mundo OSCURO deliberado (sala oscura). Fondo: el render 3D de la vaca
// (public/render.mp4) MUY difuminado → estética futurista. Dos estados:
//   · PRE-APERTURA: sin ningún dato (ni sample). Cuenta regresiva innovadora que
//     corre en [countdown_at, opens_at) del contrato; antes de countdown_at,
//     "standby" SIN número (la cuenta arranca de sorpresa).
//   · EN VIVO (desde opens_at / bidding abierto): dashboard — anillo de cupo,
//     curva acumulada y ranking de compradores (sin PII, solo display_name).
// PROVISIONAL(Q-320) — lectores tolerantes compartidos con la experiencia mobile.

const ARC_LEN = 1055;
const ROW_H = 68;
const TOP_N = 8;

// Tween rAF (easeOutCubic) para el número del cupo — from = último valor
// asentado (fromRef, actualizado en effect, nunca en render).
function useTween(target, ms = 600) {
    const [val, setVal] = useState(target);
    const fromRef = useRef(target);
    useEffect(() => {
        const from = fromRef.current;
        const start = performance.now();
        let raf;
        const step = (now) => {
            const p = Math.min(1, (now - start) / ms);
            const e = 1 - Math.pow(1 - p, 3);
            setVal(from + (target - from) * e);
            if (p < 1) raf = requestAnimationFrame(step);
            else fromRef.current = target;
        };
        raf = requestAnimationFrame(step);
        return () => { cancelAnimationFrame(raf); fromRef.current = target; };
    }, [target, ms]);
    return val;
}

function GaugeArc({ frac }) {
    return (
        <svg className="ev-gauge__svg" viewBox="0 0 380 380">
            <circle cx="190" cy="190" r="168" fill="none" stroke="rgba(255,255,255,.06)" strokeWidth="26" />
            <circle
                cx="190" cy="190" r="168" fill="none" stroke="url(#evg)" strokeWidth="26" strokeLinecap="round"
                strokeDasharray={ARC_LEN} strokeDashoffset={ARC_LEN * (1 - Math.max(0, Math.min(1, frac)))}
                className="ev-gauge__arc"
            />
            <defs>
                <linearGradient id="evg" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0" stopColor="#04c2a0" /><stop offset="1" stopColor="#5cc8f5" />
                </linearGradient>
            </defs>
        </svg>
    );
}

function Spark({ series, max }) {
    const W = 1100, H = 190, n = series.length;
    const x = (i) => (n <= 1 ? 0 : (i / (n - 1)) * W);
    const y = (v) => H - (v / (max || 1)) * (H - 10) - 4;
    const d = series.map((p, i) => `${i ? 'L' : 'M'} ${x(i).toFixed(1)} ${y(p.v).toFixed(1)}`).join(' ');
    const lx = x(n - 1), ly = y(series[n - 1]?.v ?? 0);
    return (
        <svg className="ev-spark__svg" viewBox="0 0 1100 190" preserveAspectRatio="none">
            <path d={`${d} L ${W} ${H} L 0 ${H} Z`} fill="url(#evga)" stroke="none" />
            <path d={d} fill="none" stroke="#22e0bd" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
            <circle cx={lx} cy={ly} r="6" fill="#22e0bd" className="ev-spark__dot" />
            <defs>
                <linearGradient id="evga" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0" stopColor="rgba(34,224,189,.34)" /><stop offset="1" stopColor="rgba(34,224,189,0)" />
                </linearGradient>
            </defs>
        </svg>
    );
}

// Cuenta regresiva de proyección — SIN datos del evento. Anillo de progreso +
// número gigante que late por segundo, orbitas decorativas y el render 3D
// difuminado detrás. Antes de countdown_at: "standby" sin número (sorpresa).
function CountdownStage({ counting, secondsLeft, progress, eventName }) {
    const R = 300;
    const C = 2 * Math.PI * R;
    return (
        <div className={`evc ${counting ? 'evc--counting' : 'evc--standby'}`}>
            <div className="evc__brand">
                <img className="evc__brand-logo" src="/cowre.png" alt="" aria-hidden="true" />
                <span className="evc__brand-txt">
                    <span className="evc__brand-name">COWRE</span>
                    <span className="evc__brand-sub">{eventName || 'Subasta de genética en vivo'}</span>
                </span>
            </div>

            <div className="evc__core">
                <span className="evc__orbit evc__orbit--1" aria-hidden="true" />
                <span className="evc__orbit evc__orbit--2" aria-hidden="true" />
                <span className="evc__glow" aria-hidden="true" />
                <svg className="evc__ring" viewBox="0 0 700 700" aria-hidden="true">
                    <circle className="evc__ring-track" cx="350" cy="350" r={R} />
                    {counting && (
                        <circle
                            className="evc__ring-arc" cx="350" cy="350" r={R}
                            strokeDasharray={C}
                            strokeDashoffset={C * Math.min(1, Math.max(0, progress))}
                            transform="rotate(-90 350 350)"
                        />
                    )}
                </svg>
                <div className="evc__center">
                    {counting ? (
                        <>
                            <span className="evc__eyebrow">La puja abre en</span>
                            <span key={secondsLeft} className="evc__num">{secondsLeft}</span>
                            <span className="evc__unit">segundos</span>
                        </>
                    ) : (
                        <>
                            <img className="evc__logo" src="/cowre.png" alt="COWRE" />
                            <span className="evc__standby-txt">Preparando la subasta</span>
                            <span className="evc__dots" aria-hidden="true"><i /><i /><i /></span>
                            <span className="evc__standby-sub">En vivo en instantes</span>
                        </>
                    )}
                </div>
            </div>

            <div className="evc__foot">Escanea el QR y participa desde tu celular</div>
        </div>
    );
}

export default function EventScreen({ token }) {
    const qc = useQueryClient();
    const stageRef = useRef(null);
    const { data, isError, error } = useEventDashboard(token);

    // Countdown recibido por el WS (setCd solo desde el handler del socket, nunca
    // en un efecto). market_event:state → snapshot a caché; market_event:countdown
    // → solo el bloque. join_event ack se enruta como state (ver event.jsx).
    const [cd, setCd] = useState(null);
    const onWsEvent = useCallback((evName, args) => {
        const payload = args?.[0];
        if (!payload) return;
        if (evName === 'market_event:state') {
            qc.setQueryData(eventKeys.dashboard(token), payload);
            if (payload.countdown) setCd(normalizeCountdown(payload.countdown));
        } else if (evName === 'market_event:countdown') {
            setCd(normalizeCountdown(payload));
        }
    }, [qc, token]);
    const connected = useEventSocket(
        token,
        () => qc.invalidateQueries({ queryKey: eventKeys.dashboard(token) }),
        onWsEvent,
    );

    const { event, offerings, currentOfferingId } = useMemo(() => readEventState(data), [data]);
    // Vaca en foco = la fijada por el subastador (current_offering_id); cambia al
    // instante mid-puja cuando el server difunde el nuevo foco.
    const focal = useMemo(() => pickFocal(offerings, currentOfferingId), [offerings, currentOfferingId]);
    const ranking = useMemo(() => aggregateBuyers(offerings).slice(0, TOP_N), [offerings]);
    // Respaldo por polling (sin efecto): el WS (cd) tiene prioridad.
    const dashCd = useMemo(() => normalizeCountdown(readCountdown(data)), [data]);
    const countdown = cd ?? dashCd;

    // Reloj (100ms) para la cuenta regresiva.
    const [now, setNow] = useState(() => Date.now());
    useEffect(() => {
        const t = setInterval(() => setNow(Date.now()), 100);
        return () => clearInterval(t);
    }, []);

    // Estado de fase. La cuenta regresiva MANDA: si está armada y aún no abre, la
    // pantalla está pre-apertura (aunque el state diga 'open'). Una vez abierta la
    // puja (opens_at pasado, o el evento/oferta ya abiertos) → dashboard.
    const opensAt = countdown?.armed ? countdown.opensAt : null;
    const armed = opensAt != null;
    const beforeOpen = armed && now < opensAt;
    const counting = beforeOpen && countdown.countdownAt != null && now >= countdown.countdownAt;
    const biddingOpen = event.state === 'open' || offerings.some((o) => o.state === 'open');
    const isLive = !beforeOpen && (biddingOpen || (armed && now >= opensAt));

    const secondsLeft = counting ? Math.max(1, Math.ceil((opensAt - now) / 1000)) : 0;
    const total = (opensAt != null && countdown?.countdownAt != null)
        ? (opensAt - countdown.countdownAt) : ((countdown?.seconds || 20) * 1000);
    const progress = counting ? Math.min(1, Math.max(0, (now - countdown.countdownAt) / total)) : 0;

    // scale-to-fit 1920×1080
    useEffect(() => {
        const fit = () => {
            if (!stageRef.current) return;
            const s = Math.min(window.innerWidth / 1920, window.innerHeight / 1080);
            stageRef.current.style.setProperty('--s', s);
        };
        fit();
        window.addEventListener('resize', fit);
        return () => window.removeEventListener('resize', fit);
    }, []);

    // serie acumulada del sold de la vaca focal (reset al cambiar de vaca); solo
    // relevante en vivo (pre-apertura no hay datos).
    const [series, setSeries] = useState([{ t: 0, v: 0 }]);
    const focalIdRef = useRef(null);
    useEffect(() => {
        if (!focal) return;
        if (focalIdRef.current !== focal.id) {
            focalIdRef.current = focal.id;
            setSeries([{ t: 0, v: focal.sold }]);
        } else {
            setSeries((s) => {
                if (s[s.length - 1]?.v === focal.sold) return s;
                const next = [...s, { t: Date.now(), v: focal.sold }];
                return next.length > 70 ? next.slice(-70) : next;
            });
        }
    }, [focal]);

    const frac = focal && focal.offered > 0 ? focal.sold / focal.offered : 0;
    const cupo = useTween(Math.round(frac * 100));

    if (isError) {
        return (
            <div className="ev-screen ev-screen--msg">
                {error?.status === 404 ? 'Evento no encontrado o cerrado.' : 'No se pudo cargar el evento.'}
            </div>
        );
    }

    return (
        <div className={`ev-screen ${isLive ? 'ev-screen--live' : 'ev-screen--pre'}`}>
            {/* Fondo: render 3D difuminado (public/render.mp4). Muted+loop autoplay. */}
            <video className="ev-bg" src="/render.mp4" autoPlay loop muted playsInline aria-hidden="true" />
            <div className="ev-bg__veil" aria-hidden="true" />

            <div className="ev-stage" ref={stageRef}>
                {!isLive ? (
                    <CountdownStage counting={counting} secondsLeft={secondsLeft} progress={progress} eventName={event.name} />
                ) : (
                    <>
                        <header className="ev-top">
                            <div className="ev-brand">
                                <img className="ev-brand__logo" src="/cowre.png" alt="" aria-hidden="true" />
                                <span>
                                    <span className="ev-brand__name">COWRE</span>
                                    <span className="ev-brand__sub">{event.name || 'Subasta de genética en vivo'}</span>
                                </span>
                            </div>
                            <div className="ev-live">
                                <span className={`ev-live__dot ${connected ? 'ev-live__dot--on' : ''}`} />
                                <span className="ev-live__txt">{connected ? 'En vivo' : 'Conectando'}</span>
                            </div>
                        </header>

                        <main className="ev-main">
                            <section className="ev-hero">
                                {/* Render 3D detrás de la donadora — más chico, contenido y con
                                    fade radial para que la vaca "flote" sin tapar los datos. */}
                                <video className="ev-hero__render" src="/render.mp4" autoPlay loop muted playsInline aria-hidden="true" />
                                {focal ? (
                                    <>
                                        <div className="ev-hero__head">
                                            <div>
                                                <div className="ev-hero__eyebrow">Donadora en oferta</div>
                                                <h1 className="ev-hero__name">{focal.name}</h1>
                                                {(focal.register || focal.number) && (
                                                    <div className="ev-hero__reg">{focal.register || `Nº ${focal.number}`}</div>
                                                )}
                                            </div>
                                            {focal.price != null && (
                                                <div className="ev-price">
                                                    <div className="ev-price__val">{fmtMoney(focal.price)}</div>
                                                    <div className="ev-price__lbl">por 1% de la vaca</div>
                                                </div>
                                            )}
                                        </div>

                                        <div className="ev-gaugewrap">
                                            <div className="ev-gauge">
                                                <GaugeArc frac={frac} />
                                                <div className="ev-gauge__center">
                                                    <div className="ev-gauge__pct">{Math.round(cupo)}<small>%</small></div>
                                                    <div className="ev-gauge__lbl">del cupo colocado</div>
                                                </div>
                                            </div>
                                            <div className="ev-chips">
                                                <div className="ev-chip ev-chip--taken"><div className="ev-chip__val">{focal.sold}%</div><div className="ev-chip__lbl">Tomado de la vaca</div></div>
                                                <div className="ev-chip ev-chip--free"><div className="ev-chip__val">{focal.free}%</div><div className="ev-chip__lbl">Libre</div></div>
                                                <div className="ev-chip"><div className="ev-chip__val">{focal.offered}%</div><div className="ev-chip__lbl">Ofertado</div></div>
                                            </div>
                                        </div>

                                        <div className="ev-spark">
                                            <div className="ev-spark__head">
                                                <span className="ev-spark__title">% de la vaca tomado · en el tiempo</span>
                                                <span className="ev-spark__now">{focal.sold}%</span>
                                            </div>
                                            <Spark series={series} max={focal.offered} />
                                        </div>
                                    </>
                                ) : (
                                    <div className="ev-hero__wait">Esperando la próxima donadora…</div>
                                )}
                            </section>

                            <aside className="ev-rank">
                                <div className="ev-rank__head">
                                    <div className="ev-rank__title">Ranking de <span>compradores</span></div>
                                    <div className="ev-rank__count">{ranking.length} participantes</div>
                                </div>
                                <div className="ev-board" style={{ height: `${ranking.length * ROW_H}px` }}>
                                    {ranking.map((b, i) => {
                                        const lead = ranking[0]?.pct || 1;
                                        return (
                                            <div key={b.name} className="ev-row" data-rank={i + 1} style={{ top: `${i * ROW_H}px` }}>
                                                <span className="ev-row__badge">{i + 1}</span>
                                                <span className="ev-row__name">{b.name}</span>
                                                <span className="ev-row__track">
                                                    <span className="ev-row__fill" style={{ width: `${Math.max(4, (b.pct / lead) * 100)}%` }} />
                                                </span>
                                                <span className="ev-row__pct">{b.pct}%</span>
                                            </div>
                                        );
                                    })}
                                    {ranking.length === 0 && <div className="ev-board__empty">Aún no hay pujas.</div>}
                                </div>
                            </aside>
                        </main>
                    </>
                )}
            </div>
        </div>
    );
}
